/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: Dashboard (C) Copyright IBM Corp. 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'underscore'
], function (_) {

	/**
	 * Transient state wrapper to integrate with React-style state
	 * We need to save and read transient state which has no references to React components as the state can outlive the React component.
	 * The TransientStateManger is for use when it would be desireable for state to have references to React components.
	 * It maps these two needs by indirection. Set the state using a "descriptor" which has a type, and an arbitrary payload (without references).
	 * Then, use the typeToCreator map to provide a function that converts a "descriptor" to a useful "value" that may have references to React components.
	 * This way, it is safe for the descriptors to outlive their React components, and they can be converted to useful payload on-the-fly. 
	 */
	class TransientStateManager {
		constructor({
			transientState,
			transientStateKey,
			typeToCreator,
			onStateChange
		}) {
			this._transientState = transientState;
			this._transientStateKey = transientStateKey;
			this._typeToCreator = typeToCreator;
			this._onStateChange = onStateChange;
			this._descriptors = [];
			this._values = [];
			this._init();
		}

		_init() {
			const preexistingStateDescriptor = this.getCurrentDescriptor();
			if (preexistingStateDescriptor) {
				this._prepareCurrentValue(preexistingStateDescriptor);
			}
		}

		_prepareCurrentValue(descriptor) {
			if (!this._getValue(descriptor)) {
				const { type, payload } = descriptor;
				this._setValue(
					descriptor,
					this._typeToCreator[type](payload)
				);
			}
		}

		_getIndex(descriptor) {
			for(let i = 0; i < this._descriptors.length; i++) {
				if(_.isEqual(this._descriptors[i], descriptor)) {
					return i;
				}
			}
			return -1;
		}

		_getValue(descriptor) {
			return this._values[this._getIndex(descriptor)];
		}

		_setValue(descriptor, value) {
			this._descriptors.push(descriptor);
			this._values.push(value);
		}

		setCurrentDescriptor(type, payload={}) {
			const descriptor = { type, payload };
			this._transientState.set(this._transientStateKey, descriptor);
			this._prepareCurrentValue(descriptor);
			this._onStateChange();
		}

		getCurrentDescriptor() {
			return this._transientState.get(this._transientStateKey);
		}

		getCurrentValue() {
			const descriptor = this.getCurrentDescriptor();
			if(!descriptor) {
				return;
			}
			return this._getValue(descriptor);
		}

		getValues() {
			return this._values;
		}

		removeDescriptor(type, payload) {
			const currentDescriptorIndex = this._getIndex(this.getCurrentDescriptor());
			const specifiedDescriptorIndex = this._getIndex({ type, payload });
			if (specifiedDescriptorIndex >= 0) {
				this._descriptors.splice(specifiedDescriptorIndex, 1);
				this._values.splice(specifiedDescriptorIndex, 1);
				if(currentDescriptorIndex === specifiedDescriptorIndex) {
					this._transientState.set(this._transientStateKey, null);
				}
				this._onStateChange();
			}
		}

		destroy() {
			delete this._transientState;
			delete this._typeToCreator;
			delete this._onStateChange;
			delete this._descriptors;
			delete this._values;
		}
	}

	return TransientStateManager;
});
